import { Theme } from "@emotion/react";
import { ButtonProps, SxProps } from "@mui/material";
import { CSSProperties, useState } from "react";
import { createDialogConfig as buildDialogConfig } from "../config/dialog.config";
import { IButtonProps, IDialogProps } from "../interfaces/dialog.interfaces";

const mergeSx = (...styles: Array<CSSProperties | SxProps<Theme> | undefined>): SxProps<Theme> => {
    return styles.filter(Boolean) as SxProps<Theme>;
};

/**
 * Hook to manage the dialog state and general logic
 */
export const useDialog = () => {
    const [open, setOpen] = useState(false);
    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
    }

    return {
        open,
        handleOpen,
        handleClose,
    }
};

/** 
 * Hook to manage the dialog props and the styles
 */
export const useDialogProps = ({ onClose, styles, config, dismissible = true }: Pick<IDialogProps, "onClose" | "styles" | "config" | "dismissible">) => {
    const dialogConfig = buildDialogConfig(config);

    const handleClose = () => {
        if (!dismissible) {
            return;
        }

        onClose();
    };

    const getButtonProps = (button: IButtonProps): ButtonProps => {
        const buttonConfig = dialogConfig.buttons?.[button.variant];
        const buttonStyleKey = button.variant === "primary" ? "primaryButton" : "secondaryButton";
        const buttonStyles = dialogConfig.styles?.[buttonStyleKey];

        return {
            ...buttonConfig,
            onClick: button.onClick,
            disabled: button.disabled,
            sx: mergeSx(buttonStyles, button.styles),
        };
    };

    return {
        dialogConfig,
        handleClose,
        getButtonProps,
        dialogSx: mergeSx(dialogConfig.styles?.dialog, styles),
        dismissible,
    };
};
